import { useLocale, useTexts } from '~/shared/libs/gatsby-plugin-i18n';
import styled from '@emotion/styled';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';

import ContentBox from '~/components/ContentBox';
import SectionBase, { SectionBaseProps } from '~/widgets/sections/base';
import ThumbnailCard from '~/components/ThumbnailCard';
import { css } from '~/css';
import { useFrontmatter } from '~/shared/contexts/frontmatter';
import type { HotIssueQuery } from '~/generated/types/gatsby';
import fromPairs from '~/shared/utils/fromPairs';
import { mq } from '~/shared/utils/mq';
import { skin } from '~/theme';

export interface HotIssueSectionProps extends SectionBaseProps {
  moreUrl?: string;
}

const Container = styled(SectionBase)(
  skin({
    blackSquare: css({
      background: 'bg2',
    }),
  }),
);

const PcMoreLink = styled('a')(
  skin({
    blackSquare: css({
      color: '#000',
      textDecoration: 'none',
      display: ['none', 'inline'],
      position: 'absolute',
      fontSize: 20,
      top: '-3.5em',
      right: '0.8em',
      whiteSpace: 'nowrap',
    }),
  }),
);

const MobileMoreLink = styled('a')(
  skin({
    blackSquare: css({
      display: ['inline-block', 'none'],
      color: '#161515',
      paddingY: 16,
      fontSize: 24,
      fontWeight: 400,
      textDecoration: 'none',
      border: '1px solid #ddd',
      background: '#fff',
      width: '100%',
      textAlign: 'center',
      ':lang(ja)': {
        fontWeight: 700,
      },
      ':lang(zh-Hant)': {
        fontWeight: 700,
      },
      ':lang(th)': {
        fontWeight: 700,
      },
    }),
  }),
);

const AutoGrid = styled('div')(
  // Autoprefixer plugin 을 붙일 수가 없다니 끔-찍 하네요.
  css({
    display: '-ms-grid',
    msGridColumns: ['1fr', '1fr 1fr', '1fr 1fr 1fr 1fr'],
    msGridRows: ['1fr 1fr 1fr 1fr', '1fr 1fr', '1fr'],
    '> *': {
      margin: 15,
    },
    '@supports (display: grid)': {
      '&& > *': {
        margin: 0,
      },
    },
    ...fromPairs(
      [...Array(4)]
        .fill(null)
        .map((_, i) => i)
        .map((i) => [
          `> *:nth-of-type(${i + 1})`,
          {
            msGridColumn: [`${(i % 1) + 1}`, `${(i % 2) + 1}`, `${(i % 4) + 1}`],
            msGridRow: [`${~~(i / 1) + 1}`, `${~~(i / 2) + 1}`, `${~~(i / 4) + 1}`],
          },
        ]),
    ),
  }),
  mq({
    display: 'grid',
    gap: 30,
    gridGap: 30,
    marginBlock: 20,
    gridTemplateColumns: ['repeat(1, 1fr)', 'repeat(2, 1fr)', 'repeat(4, 1fr)'],
  }),
);

const HotIssueSection: React.FC<HotIssueSectionProps> = ({ id, title, description, moreUrl }) => {
  const gameId = useFrontmatter().gameId;
  const t = useTexts();
  const locale = useLocale();
  const data = useStaticQuery<HotIssueQuery>(graphql`
    query HotIssue {
      allStrapiGamesiteHotissue {
        nodes {
          game
          environment
          locale
          hotissues {
            title
            link
            image {
              localFile {
                childImageSharp {
                  gatsbyImageData(width: 500, breakpoints: [500], tracedSVGOptions: { optTolerance: 1, turdSize: 200 })
                }
              }
            }
          }
        }
      }
    }
  `);

  const localIssues = data.allStrapiGamesiteHotissue.nodes
    ?.find(
      (node: any) =>
        node.environment === (process.env.GATSBY_ENVIRONMENT ?? 'preview') &&
        node.game === gameId &&
        node.locale.toLowerCase() === locale.toLowerCase(),
    )
    ?.hotissues.slice(0, 4);

  return (
    <Container id={id} title={title} description={description}>
      <ContentBox>
        <div
          css={{
            position: 'relative',
          }}
        >
          {moreUrl && (
            <PcMoreLink href={moreUrl} target='_blank' rel='noopener' className='hotissue-more'>
              {t`section.hotissue.more`}
            </PcMoreLink>
          )}
          <AutoGrid>
            {localIssues?.map((issue, i) => (
              <ThumbnailCard
                key={`thumbnail-card-${i}`}
                url={issue.link}
                caption={issue.title}
                sharp={issue.image.localFile.childImageSharp}
              />
            ))}
          </AutoGrid>
          {moreUrl && (
            <MobileMoreLink href={moreUrl} className='font-cookierun-bold'>
              {t`section.hotissue.more`}
            </MobileMoreLink>
          )}
        </div>
      </ContentBox>
    </Container>
  );
};

export default HotIssueSection;
